import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type {
  ProfileOutput,
  UpdateProfileInput,
  UpdateProfileOutput,
  ChangePasswordInput,
  ChangePasswordOutput,
} from '@shared/validators/profile.schema';

/*
 * Profile-data-kerros (Sessio 16, Settings/Profiili + Salasana).
 *
 * Hookit:
 *  - useProfileQuery -> GET /api/me/profile
 *  - useUpdateProfileMutation -> PATCH /api/me/profile
 *  - useChangePasswordMutation -> PATCH /api/me/password
 *
 * Mutaatiot invalidoivat profile-queryn.
 */

const QUERY_KEYS = {
  profile: ['me', 'profile'] as const,
};

const PROFILE_STALE_MS = 30 * 1000;

export function useProfileQuery() {
  return useQuery<ProfileOutput>({
    queryKey: QUERY_KEYS.profile,
    queryFn: ({ signal }) => apiFetch<ProfileOutput>('/api/me/profile', { signal }),
    staleTime: PROFILE_STALE_MS,
  });
}

export function useUpdateProfileMutation() {
  const queryClient = useQueryClient();
  return useMutation<UpdateProfileOutput, Error, UpdateProfileInput>({
    mutationFn: (input) =>
      apiFetch<UpdateProfileOutput>('/api/me/profile', { method: 'PATCH', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.profile });
    },
  });
}

export function useChangePasswordMutation() {
  return useMutation<ChangePasswordOutput, Error, ChangePasswordInput>({
    mutationFn: (input) =>
      apiFetch<ChangePasswordOutput>('/api/me/password', { method: 'PATCH', body: input }),
  });
}
